import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAlerts } from '@src/api/manualAlerts';
import { get, isEmpty } from 'lodash';
import { manualAlertAtom } from '@src/atoms/toggles';
import { useAtomValue } from 'jotai';
import { intervalSelector } from '@store/common/selectors';
import { useSelector } from 'react-redux';
import { formatResponse } from '@components/alert-panel/ManualAlert/hooks/utils';
import { trackQueryError } from '@utils/sensor/apptracker';
import { DATA_TAGS, useGetData, useSetData } from '../useData';
import { message } from 'antd';

const defaultValue = { events: [], alerts: {}, eventsByMetrics: {} };

// fetch all alerts and their events
function useFetchManualAlerts({ enabled = false, dataTag = DATA_TAGS.manualAlertEvents }) {
  const interval = useSelector(intervalSelector);
  const alertEnabled = useAtomValue(manualAlertAtom);
  const setManualAlertData = useSetData({ tag: dataTag, defaultValue });

  // stale time prevents redundant calls from multiple subscribers
  // interval change or mutations will force a refetch
  const {
    data: response,
    isFetching,
    isError,
  } = useQuery(
    ['manual-alert-events', interval],
    () => {
      return getAlerts({ params: { from: interval.from, to: interval.to } }).then((response) => {
        const data = get(response, ['data'], {});
        const formattedResponse = formatResponse(data);
        // CE-10544 Show warning when event more than 1000
        if (formattedResponse.events.length >= 1000) {
          message.warning('Showing last 1000 alerts', 3);
        }
        return formattedResponse;
      });
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: enabled && alertEnabled && !!interval,
      staleTime: 5 * 60 * 1000, // 5m
      // notifyOnChangeProps: 'tracked',
      placeholderData: defaultValue,
      onError: (err) => {
        trackQueryError({ type: 'manual-alert-events', err });
      },
    },
  );

  const data = useMemo(() => {
    // to remove the series markers (toggled)
    if (!alertEnabled || isEmpty(response)) {
      return defaultValue;
    }
    return response;
  }, [alertEnabled, response]);

  useEffect(() => {
    setManualAlertData(data);
  }, [data, setManualAlertData]);

  return { data, isFetching, isError };
}

export const useManualAlertEventData = ({
  metric = null,
  dataTag = DATA_TAGS.manualAlertEvents,
}) => {
  const data = useGetData({ tag: dataTag, defaultValue });
  // return all events or events relevant to the metric
  if (metric) {
    const { eventsByMetrics, alerts } = data;
    const metricEvents = metric in eventsByMetrics ? eventsByMetrics[metric] : [];
    return { events: metricEvents, alerts };
  }
  return data;
};

export default useFetchManualAlerts;
